import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import {
  MessageSquare, AlertCircle, RefreshCcw, Send, User, Users, Search, X, Check, Loader2, Mail, CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminMessages = () => {
  const [activeMessageTab, setActiveMessageTab] = useState('feedback');
  const [feedbackReports, setFeedbackReports] = useState([]);
  const [advisorMessages, setAdvisorMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Toplu mesaj state'leri
  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [bulkMessageType, setBulkMessageType] = useState('all'); // 'all' veya 'selected'
  const [bulkMessageText, setBulkMessageText] = useState('');
  const [bulkMessageSubject, setBulkMessageSubject] = useState('');
  const [sendingBulkMessage, setSendingBulkMessage] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(false);

  const loadMessages = async () => {
    setMessagesLoading(true);
    try {
      const [feedbackRes, messagesRes, unreadRes] = await Promise.all([
        axios.get(`${API}/admin/feedback-reports`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/advisor-messages`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/advisor/unread-count`, { withCredentials: true }).catch(() => ({ data: { unread_count: 0 } }))
      ]);
      setFeedbackReports(feedbackRes.data || []);
      setAdvisorMessages(messagesRes.data || []);
      setUnreadCount(unreadRes.data?.unread_count || 0);
    } catch (err) {
      console.error('Mesajlar yüklenemedi:', err);
    }
    setMessagesLoading(false);
  };

  // Danışman mesajları sekmesine geçince mesajları okundu işaretle
  const handleAdvisorTabClick = async () => {
    setActiveMessageTab('advisor');
    if (unreadCount > 0) {
      try {
        await axios.post(`${API}/admin/advisor/mark-read`, {}, { withCredentials: true });
        setUnreadCount(0);
      } catch (err) {
        console.error('Mesajlar okundu işaretlenemedi:', err);
      }
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  // Öğrenci listesini yükle (toplu mesaj için)
  const loadStudents = async () => {
    setLoadingStudents(true);
    try {
      const res = await axios.get(`${API}/admin/students`, { withCredentials: true });
      // API bir array dönüyor olmalı, eğer değilse boş array kullan
      const studentData = Array.isArray(res.data) ? res.data : (res.data?.students || []);
      setStudents(studentData);
    } catch (err) {
      console.error('Öğrenciler yüklenemedi:', err);
      setStudents([]);
    }
    setLoadingStudents(false);
  };

  // Toplu mesaj modalını aç
  const openBulkMessageModal = (type) => {
    setBulkMessageType(type);
    setShowBulkMessageModal(true);
    setBulkMessageText('');
    setBulkMessageSubject('');
    setSelectedStudents([]);
    if (type === 'selected') {
      loadStudents();
    }
  };

  // Öğrenci seçimi toggle
  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Tümünü seç/kaldır
  const toggleSelectAll = () => {
    const studentList = Array.isArray(students) ? students : [];
    const filteredStudents = studentList.filter(s => 
      s.name?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
      s.email?.toLowerCase().includes(studentSearchQuery.toLowerCase())
    );
    
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  // Toplu mesaj gönder
  const sendBulkMessage = async () => {
    if (!bulkMessageText.trim()) {
      toast.error('Mesaj içeriği boş olamaz');
      return;
    }

    if (bulkMessageType === 'selected' && selectedStudents.length === 0) {
      toast.error('En az bir öğrenci seçmelisiniz');
      return;
    }

    setSendingBulkMessage(true);
    try {
      const payload = {
        message: bulkMessageText,
        subject: bulkMessageSubject || 'Duyuru',
        send_to: bulkMessageType === 'all' ? 'all' : 'selected',
        student_ids: bulkMessageType === 'selected' ? selectedStudents : []
      };

      const res = await axios.post(`${API}/admin/bulk-message`, payload, { withCredentials: true });
      
      if (res.data.success) {
        toast.success(`${res.data.sent_count} öğrenciye mesaj gönderildi`);
        setShowBulkMessageModal(false);
        setBulkMessageText('');
        setBulkMessageSubject('');
        setSelectedStudents([]);
      } else {
        toast.error(res.data.message || 'Mesaj gönderilemedi');
      }
    } catch (err) {
      toast.error('Mesaj gönderilirken hata oluştu');
      console.error(err);
    }
    setSendingBulkMessage(false);
  };

  const handleReply = async (studentId) => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/advisor/reply`, {
        student_id: studentId,
        message: replyText
      }, { withCredentials: true });
      toast.success('Cevap gönderildi');
      setReplyingTo(null);
      setReplyText('');
      loadMessages();
    } catch (err) {
      toast.error('Cevap gönderilemedi');
    }
    setSendingReply(false);
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'bug': return '🐛';
      case 'suggestion': return '💡';
      case 'question': return '❓';
      default: return '📝';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'bug': return 'Hata';
      case 'suggestion': return 'Öneri';
      case 'question': return 'Soru';
      default: return 'Diğer';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-red-100 text-red-700';
      case 'in_progress': return 'bg-yellow-100 text-yellow-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  if (messagesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Mesajları kullanıcıya göre grupla
  const groupedMessages = {};
  advisorMessages.forEach(msg => {
    if (!groupedMessages[msg.user_id]) {
      groupedMessages[msg.user_id] = {
        user_id: msg.user_id,
        user_name: msg.user_name || 'Anonim',
        user_email: msg.user_email || '',
        messages: []
      };
    }
    groupedMessages[msg.user_id].messages.push(msg);
  });

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveMessageTab('feedback')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeMessageTab === 'feedback'
              ? 'bg-red-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <AlertCircle className="w-4 h-4" />
          Sorun Bildirimleri
          {feedbackReports.length > 0 && (
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{feedbackReports.length}</span>
          )}
        </button>
        <button
          onClick={handleAdvisorTabClick}
          className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeMessageTab === 'advisor'
              ? 'bg-green-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <MessageSquare className="w-4 h-4" />
          Danışman Mesajları
          {Object.keys(groupedMessages).length > 0 && (
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{Object.keys(groupedMessages).length}</span>
          )}
          {/* Okunmamış mesaj badge */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        
        {/* Toplu Mesaj Butonları */}
        <div className="flex gap-2 ml-auto">
          <Button 
            onClick={() => openBulkMessageModal('all')} 
            variant="outline" 
            size="sm"
            className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
            data-testid="bulk-message-all-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            Tüm Öğrencilere Mesaj
          </Button>
          <Button 
            onClick={() => openBulkMessageModal('selected')} 
            variant="outline" 
            size="sm"
            className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
            data-testid="bulk-message-selected-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            Seçili Öğrencilere Mesaj
          </Button>
          <Button onClick={loadMessages} variant="outline" size="sm">
            <RefreshCcw className="w-4 h-4 mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Feedback Reports */}
      {activeMessageTab === 'feedback' && (
        <div className="space-y-4">
          {feedbackReports.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Henüz sorun bildirimi yok</p>
              </CardContent>
            </Card>
          ) : (
            feedbackReports.map((report, idx) => (
              <Card key={idx} className={"border-0 shadow-lg hover:shadow-xl transition-shadow " + (report.status === 'resolved' ? 'bg-green-50 border-l-4 border-l-green-500' : '')}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">{getTypeIcon(report.type)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                          {report.status === 'new' ? 'Yeni' : report.status === 'resolved' ? '✓ Çözüldü' : report.status}
                        </span>
                        <span className="text-sm text-gray-500">{getTypeLabel(report.type)}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-400">{report.page}</span>
                      </div>
                      <p className="text-gray-800 mb-2">{report.message}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {report.user_name}
                        </span>
                        <span>{report.user_email}</span>
                        <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{report.ip_address}</span>
                        <span>{new Date(report.created_at).toLocaleString('tr-TR')}</span>
                      </div>
                      {/* Çözüldü/Tekrar Aç Butonu */}
                      <div className="mt-3 flex gap-2">
                        {report.status !== 'resolved' ? (
                          <Button 
                            size="sm" 
                            className="bg-green-500 hover:bg-green-600 text-white"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/feedback-reports/${report.id}/resolve`, {}, { withCredentials: true });
                                toast.success('Bildirim çözüldü olarak işaretlendi');
                                loadMessages();
                              } catch (err) {
                                toast.error('İşlem başarısız');
                              }
                            }}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Çözüldü
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-orange-300 text-orange-600 hover:bg-orange-50"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/feedback-reports/${report.id}/reopen`, {}, { withCredentials: true });
                                toast.success('Bildirim tekrar açıldı');
                                loadMessages();
                              } catch (err) {
                                toast.error('İşlem başarısız');
                              }
                            }}
                          >
                            <RefreshCcw className="w-4 h-4 mr-1" />
                            Tekrar Aç
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Advisor Messages */}
      {activeMessageTab === 'advisor' && (
        <div className="space-y-4">
          {Object.keys(groupedMessages).length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Henüz danışman mesajı yok</p>
              </CardContent>
            </Card>
          ) : (
            Object.values(groupedMessages).map((group, idx) => (
              <Card key={idx} className="border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                        {group.user_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-semibold">{group.user_name}</p>
                        <p className="text-sm text-gray-500">{group.user_email}</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setReplyingTo(replyingTo === group.user_id ? null : group.user_id)}
                      variant="outline" 
                      size="sm"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Cevapla
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                    {group.messages.map((msg, mIdx) => (
                      <div 
                        key={mIdx}
                        className={`p-3 rounded-lg max-w-[80%] ${
                          msg.sender === 'student' 
                            ? 'bg-blue-50 mr-auto' 
                            : 'bg-green-50 ml-auto text-right'
                        }`}
                      >
                        <p className="text-gray-800 text-sm">{msg.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {msg.sender === 'advisor' && `${msg.advisor_name} • `}
                          {new Date(msg.created_at).toLocaleString('tr-TR')}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Reply Form */}
                  {replyingTo === group.user_id && (
                    <div className="flex gap-2 pt-4 border-t">
                      <Input
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Cevabınızı yazın..."
                        className="flex-1"
                      />
                      <Button 
                        onClick={() => handleReply(group.user_id)}
                        disabled={sendingReply || !replyText.trim()}
                      >
                        {sendingReply ? 'Gönderiliyor...' : 'Gönder'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Toplu Mesaj Modal */}
      {showBulkMessageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                {bulkMessageType === 'all' ? (
                  <>
                    <Users className="w-5 h-5 text-blue-500" />
                    Tüm Öğrencilere Mesaj Gönder
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5 text-purple-500" />
                    Seçili Öğrencilere Mesaj Gönder
                  </>
                )}
              </h3>
              <button 
                onClick={() => setShowBulkMessageModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 flex-1 overflow-y-auto">
              {/* Seçili Öğrenciler (sadece selected modunda) */}
              {bulkMessageType === 'selected' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-gray-400" />
                    <Input
                      value={studentSearchQuery}
                      onChange={(e) => setStudentSearchQuery(e.target.value)}
                      placeholder="Öğrenci ara (isim veya e-posta)..."
                      className="flex-1"
                    />
                    <Button 
                      onClick={toggleSelectAll} 
                      variant="outline" 
                      size="sm"
                    >
                      {selectedStudents.length === (Array.isArray(students) ? students : []).filter(s => 
                        s.name?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
                        s.email?.toLowerCase().includes(studentSearchQuery.toLowerCase())
                      ).length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                    </Button>
                  </div>
                  
                  {loadingStudents ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border rounded-lg">
                      {(Array.isArray(students) ? students : [])
                        .filter(s => 
                          s.name?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
                          s.email?.toLowerCase().includes(studentSearchQuery.toLowerCase())
                        )
                        .map(student => (
                          <div
                            key={student.id}
                            onClick={() => toggleStudentSelection(student.id)}
                            className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 transition-colors ${
                              selectedStudents.includes(student.id) ? 'bg-purple-50' : ''
                            }`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              selectedStudents.includes(student.id) 
                                ? 'bg-purple-500 border-purple-500' 
                                : 'border-gray-300'
                            }`}>
                              {selectedStudents.includes(student.id) && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
                              {student.name?.charAt(0) || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 truncate">{student.name}</p>
                              <p className="text-sm text-gray-500 truncate">{student.email}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  
                  {selectedStudents.length > 0 && (
                    <p className="text-sm text-purple-600 font-medium">
                      {selectedStudents.length} öğrenci seçildi
                    </p>
                  )}
                </div>
              )}

              {/* Mesaj Konusu */}
              <div>
                <label className="text-gray-700 text-sm font-medium mb-2 block">Konu</label>
                <Input
                  value={bulkMessageSubject}
                  onChange={(e) => setBulkMessageSubject(e.target.value)}
                  placeholder="Mesaj konusu (opsiyonel)"
                  data-testid="bulk-message-subject"
                />
              </div>

              {/* Mesaj İçeriği */}
              <div>
                <label className="text-gray-700 text-sm font-medium mb-2 block">Mesaj İçeriği *</label>
                <textarea
                  value={bulkMessageText}
                  onChange={(e) => setBulkMessageText(e.target.value)}
                  placeholder="Mesajınızı buraya yazın..."
                  className="w-full p-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                  rows="5"
                  data-testid="bulk-message-content"
                />
              </div>

              {/* Bilgi */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  {bulkMessageType === 'all' 
                    ? '📢 Bu mesaj tüm öğrencilerin profilindeki "Danışman" sekmesinde görünecektir.'
                    : '📧 Bu mesaj seçtiğiniz öğrencilerin profilindeki "Danışman" sekmesinde görünecektir.'}
                </p>
              </div>
            </div>

            {/* Gönder Butonu */}
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              <Button 
                onClick={() => setShowBulkMessageModal(false)} 
                variant="outline"
              >
                İptal
              </Button>
              <Button
                onClick={sendBulkMessage}
                disabled={sendingBulkMessage || !bulkMessageText.trim() || (bulkMessageType === 'selected' && selectedStudents.length === 0)}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                data-testid="send-bulk-message-btn"
              >
                {sendingBulkMessage ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {bulkMessageType === 'all' ? 'Tümüne Gönder' : `${selectedStudents.length} Kişiye Gönder`}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
